import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  MapPin,
  Globe,
  Instagram,
  Map,
  Leaf,
  Calendar,
  Phone,
  XCircle,
  CheckCircle2,
  Pencil,
  BellRing,
  Mail,
  Facebook,
  Twitter,
  Music2,
  Clock,
  Shield,
  BadgeCheck,
  AlertTriangle,
  ChevronDown,
  RefreshCw,
  Save,
  UserCog,
  User as UserIcon,
  Lock,
} from "lucide-react";
import {
  fetchDispensary,
  rescanDispensary,
  getScan,
  updateDispensary,
  fetchUsers,
  bulkAssignDispensaries,
  api as apiClient,
} from "../lib/api";
import { ConfidenceBadge } from "../components/ConfidenceBadge";
import { DispensaryPeopleSection } from "../components/DispensaryPeopleSection";
import { DispensaryBusTastings } from "../components/DispensaryBusTastings";
import { InteractionTimeline } from "../components/InteractionTimeline";
import { DispensaryTasks } from "../components/DispensaryTasks";
import { NextActionStrip } from "../components/NextActionStrip";
import { SalesNotesHeroButton } from "../components/SalesNotesDialog";
import { PriorityPartnersStrip } from "../components/PriorityPartnersStrip";
import { STAGES, stageMeta } from "../lib/stages";
import { useAuth } from "../lib/AuthProvider";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

function Section({ title, children, testid }) {
  return (
    <div className="panel p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-mono mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}

function LinkChip({ href, icon: Icon, label, testid }) {
  if (!href) return null;
  return (
    <a
      data-testid={testid}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-xs font-mono text-zinc-300 hover:text-emerald-300 bg-zinc-900/60 border border-zinc-800 hover:border-emerald-700/40 rounded px-2 py-1 transition-colors duration-150"
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      <ExternalLink className="w-3 h-3 text-zinc-600" />
    </a>
  );
}

/**
 * Pill badge showing when this record was last verified by a scan.
 * Renders in 3 states:
 *  - never verified      → muted, "Never verified"
 *  - verified < 90 days  → green checkmark "Verified N days ago"
 *  - verified ≥ 90 days  → amber warning "Stale (Nd) — rescan"
 */
function VerifiedBadge({ ts }) {
  if (!ts) {
    return (
      <span
        data-testid="verified-badge-never"
        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500 bg-zinc-900/60 border border-zinc-800 px-2 py-0.5 rounded"
        title="This record has never been verified by a scan"
      >
        <Clock className="w-3 h-3" /> Never verified
      </span>
    );
  }
  const then = new Date(ts).getTime();
  const days = Math.max(0, Math.floor((Date.now() - then) / 86400000));
  const stale = days >= 90;
  return (
    <span
      data-testid={stale ? "verified-badge-stale" : "verified-badge-fresh"}
      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${
        stale
          ? "text-amber-300 bg-amber-400/10 border-amber-400/20"
          : "text-emerald-300 bg-emerald-400/10 border-emerald-400/20"
      }`}
      title={`Last verified ${new Date(ts).toLocaleString()}`}
    >
      {stale ? <AlertTriangle className="w-3 h-3" /> : <BadgeCheck className="w-3 h-3" />}
      {stale
        ? `Stale (${days}d) — rescan`
        : days === 0
        ? "Verified today"
        : days === 1
        ? "Verified yesterday"
        : `Verified ${days}d ago`}
    </span>
  );
}

/**
 * Inline pipeline stage chip with a popover stage-picker. Mounted in the
 * detail-page header alongside the Active/Closed status. Clicking it lets
 * the user reassign the account's pipeline stage without opening the
 * full Edit dialog.
 */
function StageChip({ value, onChange }) {
  const meta = stageMeta(value);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid="header-stage-chip"
        className={`h-6 px-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider border ${meta.border} ${meta.bg} ${meta.color} w-auto`}
        title="Pipeline stage — click to change"
      >
        <SelectValue>{meta.label}</SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
        {STAGES.map((s) => (
          <SelectItem key={s.value} value={s.value}>
            <span className={s.color}>{s.label}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function EditInfoDialog({ d, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(d);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(d);
  }, [open, d]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        nickname: (form.nickname || "").trim() || null,
        address: form.address || "",
        city: form.city || "",
        county: form.county || "",
        website: form.website || null,
        instagram_url: form.instagram_url || null,
        weedmaps_url: form.weedmaps_url || null,
        leafly_url: form.leafly_url || null,
        facebook_url: form.facebook_url || null,
        twitter_url: form.twitter_url || null,
        tiktok_url: form.tiktok_url || null,
        phone: form.phone || null,
        email: form.email || null,
        hours: form.hours || null,
        status: form.status,
      };
      const updated = await updateDispensary(d.id, payload);
      toast.success("Dispensary updated");
      onSaved?.(updated);
      setOpen(false);
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const F = ({ label, k, placeholder, type = "text" }) => (
    <div className="flex flex-col gap-1">
      <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
        {label}
      </Label>
      <Input
        type={type}
        value={form?.[k] || ""}
        placeholder={placeholder}
        onChange={(e) => setField(k, e.target.value)}
        className="bg-zinc-900 border-zinc-800 text-zinc-100"
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        data-testid="edit-info-btn"
        onClick={() => setOpen(true)}
        variant="outline"
        className="border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-100 rounded-md"
      >
        <Pencil className="w-4 h-4 mr-1.5" />
        Edit info
      </Button>
      <DialogContent
        data-testid="edit-info-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-800 shrink-0">
          <DialogTitle className="text-zinc-50">
            Edit dispensary information
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Manually populate any field. These values flow into Lead Generator
            reports immediately.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-6 py-4 overflow-y-auto flex-1 min-h-0">
          {/* Licensed name — read-only, sourced from DCC. The licensed
              name is NEVER editable from the UI so DCC data integrity
              stays intact. Use Nickname below to override the display. */}
          <div className="sm:col-span-2 flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 inline-flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Licensed name <span className="text-zinc-600">(from DCC)</span>
            </Label>
            <div
              data-testid="licensed-name-display"
              className="bg-zinc-900/60 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-300 cursor-not-allowed select-text"
              title="This is the licensed business name on file with the California DCC. It cannot be edited here. Use the Nickname field below to change what shows up in JackTrack."
            >
              {d.name}
            </div>
          </div>

          <div className="sm:col-span-2">
            <F
              label="Nickname (shown everywhere in JackTrack)"
              k="nickname"
              placeholder={`e.g. "Mikey's House of Flower" (optional)`}
            />
            <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
              Optional. If set, replaces the licensed name wherever JackTrack
              displays this dispensary. The licensed name stays searchable.
            </p>
          </div>

          <F label="Phone" k="phone" placeholder="(415) 555-0199" />
          <F label="Email" k="email" placeholder="info@…" type="email" />
          <F label="Hours" k="hours" placeholder="Mon-Sun 9am-9pm" />
          <div className="sm:col-span-2">
            <F label="Address" k="address" placeholder="123 Main St" />
          </div>
          <F label="City" k="city" placeholder="San Francisco" />
          <F label="County" k="county" placeholder="San Francisco" />
          <F label="Website" k="website" placeholder="https://…" />
          <F label="Instagram URL" k="instagram_url" placeholder="https://instagram.com/…" />
          <F label="Weedmaps URL" k="weedmaps_url" placeholder="https://weedmaps.com/…" />
          <F label="Leafly URL" k="leafly_url" placeholder="https://leafly.com/…" />
          <F label="Facebook URL" k="facebook_url" placeholder="https://facebook.com/…" />
          <F label="Twitter / X URL" k="twitter_url" placeholder="https://twitter.com/…" />
          <F label="TikTok URL" k="tiktok_url" placeholder="https://tiktok.com/@…" />

          <div className="flex flex-col gap-1 sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              Status
            </Label>
            <Select
              value={form?.status || "active"}
              onValueChange={(v) => setField("status", v)}
            >
              <SelectTrigger
                data-testid="edit-status"
                className="bg-zinc-900 border-zinc-800 text-zinc-100"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[10px] text-zinc-500 mt-1">
              Closed records are auto-hidden from lead generator reports and the
              default dispensary list.
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-zinc-800 bg-zinc-950 shrink-0 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
          >
            Cancel
          </Button>
          <Button
            data-testid="save-edit-info"
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}




/* ---------------------------------------------------------------------------
   Page render — redesigned 2026-06-01 for non-tech-savvy sales staff.

   Reading order, top → bottom:
     1. HERO  — the "what is this account" card. Name, status, stage,
                address, quick contact actions.
     2. TASKS — what should I do next?
     3. PEOPLE — who do I talk to here?
     4. ACTIVITY — what's happened recently?
     5. BUS TASTINGS — events scheduled/completed
     6. NOTES — internal notes
     7. (collapsed) ACCOUNT DETAILS — DCC license, all socials, hours
--------------------------------------------------------------------------- */

function StatusPill({ status }) {
  if (status === "closed") {
    return (
      <span
        data-testid="status-pill-closed"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border border-rose-500/30 bg-rose-500/10 text-rose-200"
      >
        <XCircle className="w-3.5 h-3.5" /> Closed
      </span>
    );
  }
  return (
    <span
      data-testid="status-pill-active"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
    >
      <CheckCircle2 className="w-3.5 h-3.5" /> Active
    </span>
  );
}

/** Big tappable action button used in the hero card. */
function QuickAction({ icon: Icon, label, sublabel, href, testid }) {
  if (!href) return null;
  return (
    <a
      data-testid={testid}
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="group flex items-center gap-3 px-4 py-3 rounded-lg border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900 hover:border-emerald-500/40 transition-colors min-w-[180px]"
    >
      <div className="w-9 h-9 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 group-hover:bg-emerald-500/20">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
          {label}
        </div>
        <div className="text-sm text-zinc-100 font-mono truncate">
          {sublabel}
        </div>
      </div>
    </a>
  );
}

export default function DispensaryDetail() {
  const { id } = useParams();
  const { role, user: currentUser } = useAuth();
  const isAdmin = role === "admin";
  const isDispatcher = role === "dispatcher";
  const canAssign = isAdmin || isDispatcher;
  const [d, setD] = useState(null);
  const [rescanning, setRescanning] = useState(false);
  const [stageSaving, setStageSaving] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [contactsByEmail, setContactsByEmail] = useState({});

  const load = async () => {
    const data = await fetchDispensary(id);
    setD(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  // Admin/dispatcher pull the full user roster + contact map to power
  // the assignee picker + the chip name resolution. Sales/host roles
  // can't hit /admin/users (403) — for sales users the chip falls back
  // to "their own" name below (sales reps can only see dispensaries
  // they own).
  useEffect(() => {
    if (!canAssign) return;
    Promise.all([fetchUsers(), apiClient.get("/contacts")])
      .then(([userList, contactRes]) => {
        setUsers(
          (userList || []).filter((u) =>
            ["sales", "admin", "dispatcher"].includes(u.role)
          )
        );
        const map = {};
        for (const c of contactRes.data || []) {
          if (c.email) map[c.email.toLowerCase()] = c;
        }
        setContactsByEmail(map);
      })
      .catch(() => {});
  }, [canAssign]);

  const userByContactId = useMemo(() => {
    const out = {};
    for (const u of users) {
      const c = contactsByEmail[(u.email || "").toLowerCase()];
      if (c) out[c.id] = u;
    }
    return out;
  }, [users, contactsByEmail]);

  // Resolve the assignee for the current dispensary. Admin/dispatcher
  // use the joined user roster. Sales reps fall back to their own
  // identity since they only have visibility on their own accounts.
  const assignee = useMemo(() => {
    if (!d?.assigned_to_id) return null;
    if (canAssign) {
      return userByContactId[d.assigned_to_id] || null;
    }
    // Sales user — if the dispensary is in their bucket, the owner
    // by definition is them.
    if (currentUser) {
      return currentUser;
    }
    return null;
  }, [d, canAssign, userByContactId, currentUser]);

  const reassignToUser = async (userId) => {
    if (assignSaving || !canAssign) return;
    try {
      setAssignSaving(true);
      const res = await bulkAssignDispensaries([id], userId);
      toast.success(
        userId === "unassigned"
          ? "Assignment cleared"
          : `Assigned to ${res.user_name || "rep"}`
      );
      await load();
    } catch (e) {
      toast.error("Could not reassign", {
        description: e?.response?.data?.detail || e?.message,
      });
    } finally {
      setAssignSaving(false);
    }
  };

  const rescan = async () => {
    if (rescanning) return;
    try {
      setRescanning(true);
      const s = await rescanDispensary(id);
      toast.info("Rescan started", { description: d?.name });
      const t = setInterval(async () => {
        const fresh = await getScan(s.id);
        if (fresh.status !== "running") {
          clearInterval(t);
          setRescanning(false);
          await load();
          toast.success("Rescan complete", {
            description: `${fresh.new_dates_found} new date${fresh.new_dates_found === 1 ? "" : "s"} found`,
          });
        }
      }, 1500);
    } catch {
      setRescanning(false);
      toast.error("Rescan failed");
    }
  };

  const changeStage = async (nextStage) => {
    if (stageSaving) return;
    try {
      setStageSaving(true);
      const updated = await updateDispensary(id, { account_stage: nextStage });
      setD(updated);
      toast.success("Pipeline stage updated", { description: stageMeta(nextStage).label });
    } catch {
      toast.error("Could not update stage");
    } finally {
      setStageSaving(false);
    }
  };

  if (!d) {
    return (
      <div className="max-w-5xl mx-auto space-y-4">
        <Skeleton className="h-8 w-1/3 bg-zinc-900" />
        <Skeleton className="h-40 w-full bg-zinc-900" />
        <Skeleton className="h-32 w-full bg-zinc-900" />
      </div>
    );
  }

  const meta = stageMeta(d.account_stage || "untouched");
  const mapsUrl =
    typeof d.latitude === "number" && typeof d.longitude === "number"
      ? `https://www.google.com/maps/dir/?api=1&destination=${d.latitude},${d.longitude}`
      : d.address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([d.address, d.city].filter(Boolean).join(" "))}`
      : null;

  return (
    <div className="max-w-5xl mx-auto space-y-6 fade-up" data-testid="dispensary-detail-page">
      {/* Back link */}
      <Link
        to="/dispensaries"
        className="text-xs text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to dispensaries
      </Link>

      {/* ============ HERO CARD ============ */}
      <div
        data-testid="hero-card"
        className="panel p-6 space-y-5 border border-zinc-800"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusPill status={d.status} />
              <Select
                value={d.account_stage || "untouched"}
                onValueChange={changeStage}
                disabled={stageSaving}
              >
                <SelectTrigger
                  data-testid="hero-stage-chip"
                  className={`h-7 px-2.5 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider border ${meta.border} ${meta.bg} ${meta.color} w-auto`}
                  title="Pipeline stage — click to change"
                >
                  <SelectValue>{meta.label}</SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {STAGES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      <span className={s.color}>{s.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Assigned-to chip — visible to everyone, editable by
                  admin/dispatcher. Sales reps see who owns the account
                  but cannot change it. */}
              {(() => {
                if (canAssign) {
                  return (
                    <Select
                      value={assignee?.user_id || "unassigned"}
                      onValueChange={reassignToUser}
                      disabled={assignSaving}
                    >
                      <SelectTrigger
                        data-testid="hero-assignee-chip"
                        className={`h-7 px-2.5 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider border w-auto ${
                          assignee
                            ? "border-indigo-500/30 bg-indigo-500/10 text-indigo-200"
                            : "border-amber-500/30 bg-amber-500/10 text-amber-200"
                        }`}
                        title="Assigned sales rep — click to change"
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <UserCog className="w-3 h-3" />
                          {assignee ? (assignee.name || assignee.email) : "Unassigned"}
                        </span>
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                        <SelectItem value="unassigned">
                          <span className="text-amber-300">Unassigned</span>
                        </SelectItem>
                        {users.map((opt) => (
                          <SelectItem key={opt.user_id} value={opt.user_id}>
                            <span className="inline-flex items-center gap-2">
                              {opt.name || opt.email}
                              <span className="text-[10px] uppercase font-mono text-zinc-500">
                                {opt.role}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                }
                // Sales / read-only view
                return assignee ? (
                  <span
                    data-testid="hero-assignee-chip"
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-mono uppercase tracking-wider border border-indigo-500/30 bg-indigo-500/10 text-indigo-200 rounded"
                  >
                    <UserIcon className="w-3 h-3" />
                    {assignee.name || assignee.email}
                  </span>
                ) : null;
              })()}
            </div>
            <h1
              data-testid="hero-name"
              className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-50 leading-tight break-words"
            >
              {d.nickname || d.name}
            </h1>
            {d.nickname && d.nickname !== d.name && (
              <div
                data-testid="hero-licensed-name"
                className="text-xs text-zinc-500 font-mono inline-flex items-center gap-1.5 mt-1"
                title="This is the business name on the DCC license. The display name above is a nickname set in this app."
              >
                <Lock className="w-3 h-3" />
                Licensed as{" "}
                <span className="text-zinc-400">{d.name}</span>
              </div>
            )}
            {(d.address || d.city) && (
              <a
                href={mapsUrl || "#"}
                target={mapsUrl ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid="hero-address"
                className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-emerald-300"
              >
                <MapPin className="w-3.5 h-3.5" />
                <span>
                  {[d.address, d.city, d.county && `${d.county} County`]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </a>
            )}
          </div>

          {/* Admin-only controls (top-right) */}
          {isAdmin && (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                data-testid="rescan-btn"
                onClick={rescan}
                disabled={rescanning}
                variant="outline"
                className="border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-100 rounded-md"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-1.5 ${rescanning ? "animate-spin" : ""}`}
                />
                {rescanning ? "Rescanning…" : "Rescan"}
              </Button>
              <EditInfoDialog d={d} onSaved={setD} />
            </div>
          )}
        </div>

        {/* Quick actions — call / email / directions / notes.
            Always renders so the Sales Notes button is available even
            on accounts with no phone/email/coords yet. */}
        <div className="flex items-stretch gap-2 flex-wrap pt-1">
          {d.phone && (
            <QuickAction
              icon={Phone}
              label="Call"
              sublabel={d.phone}
              href={`tel:${d.phone}`}
              testid="hero-action-call"
            />
          )}
          {d.email && (
            <QuickAction
              icon={Mail}
              label="Email"
              sublabel={d.email}
              href={`mailto:${d.email}`}
              testid="hero-action-email"
            />
          )}
          {mapsUrl && (
            <QuickAction
              icon={MapPin}
              label="Directions"
              sublabel="Open in Google Maps"
              href={mapsUrl}
              testid="hero-action-directions"
            />
          )}
          {d.website && (
            <QuickAction
              icon={Globe}
              label="Website"
              sublabel={d.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              href={d.website}
              testid="hero-action-website"
            />
          )}
          <SalesNotesHeroButton
            dispensaryId={d.id}
            dispensaryName={d.nickname || d.name}
          />
        </div>

        {/* License alert banner — surfaces immediately if license is lapsed */}
        {d.dcc_license_alert && (
          <div
            data-testid="license-alert-banner"
            className="flex items-start gap-3 p-3 rounded-md border border-rose-500/30 bg-rose-500/10"
          >
            <BellRing className="w-5 h-5 text-rose-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-medium text-rose-200">
                Heads up — state license isn't active
              </div>
              <div className="text-xs text-rose-200/80 mt-0.5">
                {d.dcc_license_alert}. Hold outreach until status is confirmed.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============ NEXT ACTION (front-and-center) ============ */}
      <NextActionStrip dispensaryId={d.id} />

      {/* ============ PRIORITY PARTNERS (silent when none) ============ */}
      <PriorityPartnersStrip dispensaryId={d.id} />

      {/* ============ PRIMARY SECTIONS (Tasks, Notes, Activity) ============ */}
      <DispensaryTasks dispensaryId={d.id} dispensary={d} />

      <InteractionTimeline dispensaryId={d.id} />

      {/* ============ SECONDARY SECTIONS (compact / supporting) ============ */}
      <DispensaryPeopleSection dispensaryId={d.id} />
      <DispensaryBusTastings dispensaryId={d.id} dispensaryName={d.nickname || d.name} />

      {/* ============ COLLAPSED: ACCOUNT DETAILS ============ */}
      <AccountDetailsPanel d={d} />
    </div>
  );
}

/**
 * Collapsible "Account details" — all the technical bits sales staff
 * don't need on first glance but admins occasionally do: DCC license
 * info, all social links, hours, created/updated dates.
 */
function AccountDetailsPanel({ d }) {
  const [open, setOpen] = useState(false);

  const hasAnyExtra =
    d.dcc_license_number ||
    d.hours ||
    d.instagram_url ||
    d.facebook_url ||
    d.twitter_url ||
    d.tiktok_url ||
    d.weedmaps_url ||
    d.leafly_url;

  if (!hasAnyExtra) return null;

  return (
    <div className="panel p-0 overflow-hidden" data-testid="account-details-panel">
      <button
        type="button"
        data-testid="account-details-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-zinc-900/50 transition-colors"
      >
        <span className="text-sm font-medium text-zinc-200 inline-flex items-center gap-2">
          <Shield className="w-4 h-4 text-zinc-500" />
          Account info & social links
        </span>
        <ChevronDown
          className={`w-4 h-4 text-zinc-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-zinc-800 space-y-5">
          {/* Hours */}
          {d.hours && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1.5">
                <Clock className="w-3 h-3 inline mr-1" /> Hours
              </div>
              <div className="text-sm text-zinc-200" data-testid="detail-hours">
                {d.hours}
              </div>
            </div>
          )}

          {/* Social links */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">
              Social & directory
            </div>
            <div className="flex flex-wrap gap-1.5">
              <LinkChip href={d.instagram_url}  icon={Instagram} label="Instagram" testid="link-instagram" />
              <LinkChip href={d.facebook_url}   icon={Facebook}  label="Facebook"  testid="link-facebook" />
              <LinkChip href={d.twitter_url}    icon={Twitter}   label="Twitter"   testid="link-twitter" />
              <LinkChip href={d.tiktok_url}     icon={Music2}    label="TikTok"    testid="link-tiktok" />
              <LinkChip href={d.weedmaps_url}   icon={Map}       label="Weedmaps"  testid="link-weedmaps" />
              <LinkChip href={d.leafly_url}     icon={Leaf}      label="Leafly"    testid="link-leafly" />
              {!d.instagram_url && !d.facebook_url && !d.twitter_url && !d.tiktok_url && !d.weedmaps_url && !d.leafly_url && (
                <span className="text-xs text-zinc-600 italic">No social profiles on file yet.</span>
              )}
            </div>
          </div>

          {/* DCC license */}
          {d.dcc_license_number && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">
                CA DCC license
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div>
                  <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">License #</div>
                  <div className="font-mono text-zinc-200 mt-0.5" data-testid="detail-dcc-license">
                    {d.dcc_license_number}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">Status</div>
                  <div className={`font-mono mt-0.5 ${
                    d.dcc_license_status?.toLowerCase() === "active"
                      ? "text-emerald-300"
                      : "text-rose-300"
                  }`}>
                    {d.dcc_license_status || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">Issued</div>
                  <div className="font-mono text-zinc-300 mt-0.5">{d.dcc_issue_date || "—"}</div>
                </div>
                <div>
                  <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">Expires</div>
                  <div className="font-mono text-zinc-300 mt-0.5">{d.dcc_expiration_date || "—"}</div>
                </div>
                {d.dcc_legal_name && d.dcc_legal_name !== d.name && (
                  <div className="col-span-2 sm:col-span-4">
                    <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">Legal name</div>
                    <div className="text-zinc-300 mt-0.5">{d.dcc_legal_name}</div>
                  </div>
                )}
                {d.dcc_owner_name && (
                  <div className="col-span-2 sm:col-span-4">
                    <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-wider">Owner of record</div>
                    <div className="text-zinc-300 mt-0.5 leading-snug">{d.dcc_owner_name}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Closure evidence (only when closed) */}
          {d.status === "closed" && d.status_evidence && (
            <div className="text-xs border border-rose-400/30 rounded-md p-3 bg-rose-400/5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] uppercase tracking-wider font-mono text-rose-300">
                  Closure evidence · {d.status_evidence.source}
                </span>
                {d.status_evidence.url && (
                  <a
                    href={d.status_evidence.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-mono text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"
                  >
                    view <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <div className="text-zinc-300 leading-relaxed italic">
                "{(d.status_evidence.snippet || "").trim()}"
              </div>
            </div>
          )}

          {/* Created/updated footer */}
          <div className="flex items-center gap-6 pt-2 border-t border-zinc-800 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            <div>
              Created · <span className="text-zinc-300">{d.created_at?.slice(0, 10) || "—"}</span>
            </div>
            <div>
              Last updated · <span className="text-zinc-300">{d.last_updated?.slice(0, 10) || "—"}</span>
            </div>
            {d.last_verified_at && (
              <div>
                <BadgeCheck className="w-3 h-3 inline mr-0.5 text-emerald-400" />
                Verified · <span className="text-zinc-300">{new Date(d.last_verified_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
